/**
 * Supplier GST registration types, stored as Tally spells them. Picking a Tally
 * ledger copies its type onto the supplier, and every pushed voucher sends the
 * supplier's type back to Tally, so the two lists must match exactly. Mirrors
 * PmsData.Helpers.GstRegistrationTypes on the server.
 *
 * "Overseas" and "SEZ" were this screen's earlier names. Tally has neither: a
 * foreign party is "Unknown" and an SEZ unit is "Regular - SEZ".
 */
export const GST_REGISTRATION_TYPES: { value: string; label: string }[] = [
  { value: 'Regular', label: 'Regular' },
  { value: 'Composition', label: 'Composition' },
  { value: 'Regular - SEZ', label: 'Regular - SEZ' },
  { value: 'Unregistered', label: 'Unregistered' },
  { value: 'Unregistered/Consumer', label: 'Unregistered/Consumer' },
  { value: 'Consumer', label: 'Consumer' },
  { value: 'Unknown', label: 'Unknown (Overseas / not set)' }
];

const LEGACY_ALIASES: Record<string, string> = {
  overseas: 'Unknown',
  sez: 'Regular - SEZ'
};

const GSTIN_MANDATORY_TYPES = ['Regular', 'Composition', 'Regular - SEZ'];

/** Tally's spelling of a type: trimmed, canonical casing, legacy names rewritten. Null when blank. */
export function normalizeGstRegistrationType(value: string | null | undefined): string | null {
  const trimmed = `${value ?? ''}`.trim();
  if (!trimmed) return null;
  const key = trimmed.toLowerCase();
  if (LEGACY_ALIASES[key]) return LEGACY_ALIASES[key];
  return GST_REGISTRATION_TYPES.find(t => t.value.toLowerCase() === key)?.value ?? trimmed;
}

/** Whether a supplier can be saved with this type. */
export function isKnownGstRegistrationType(value: string | null | undefined): boolean {
  const normalized = normalizeGstRegistrationType(value);
  return normalized !== null && GST_REGISTRATION_TYPES.some(t => t.value === normalized);
}

/** Regular, Composition and Regular - SEZ suppliers must carry a GSTIN. */
export function gstRegistrationTypeRequiresGstin(value: string | null | undefined): boolean {
  return GSTIN_MANDATORY_TYPES.includes(normalizeGstRegistrationType(value) ?? '');
}

/** A foreign (Unknown) supplier's tax number is not an Indian GSTIN, so its format is not checked. */
export function gstRegistrationTypeSkipsGstinFormat(value: string | null | undefined): boolean {
  return normalizeGstRegistrationType(value) === 'Unknown';
}

/** What the list shows for a stored type. */
export function gstRegistrationTypeLabel(value: string | null | undefined): string {
  const normalized = normalizeGstRegistrationType(value);
  return GST_REGISTRATION_TYPES.find(t => t.value === normalized)?.label ?? normalized ?? '';
}
